import {
    addDaysToDate,
    BirthdayDate,
    FrenchDate,
    formatDateAsAnniversary,
    transformFrenchToBirthDayDate,
} from 'helpers/dateTimes';

/**
 * Calcule les 7 dates de la semaine à partir d'un lundi
 * @param mondayDate La date du lundi de la semaine
 * @returns Un tableau de 7 dates au format YYYY-MM-DD
 */
export const getWeekDates = (mondayDate: Date): BirthdayDate[] => {
    const dates: BirthdayDate[] = [];

    for (let i = 0; i < 7; i++) {
        const currentDate = addDaysToDate({ date: mondayDate, daysToAdd: i });
        const dateString = formatDateAsAnniversary({ dateTime: currentDate, useNewFormat: true });
        dates.push(dateString);
    }

    return dates;
};

/**
 * Calcule les labels visuels des jours avec dates réelles
 * @param mondayDate La date du lundi de la semaine
 * @returns Un tableau d'objets avec weekday et shortDate pour affichage sur deux lignes
 */
export const getWeekLabels = (mondayDate: Date): Array<{ weekday: string; shortDate: string }> => {
    const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const labels: Array<{ weekday: string; shortDate: string }> = [];

    for (let i = 0; i < 7; i++) {
        const currentDate = addDaysToDate({ date: mondayDate, daysToAdd: i });
        const dayNumber = currentDate.getDate().toString().padStart(2, '0');
        const monthNumber = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        const weekdayName = WEEKDAYS[i];

        labels.push({
            weekday: weekdayName,
            shortDate: `${dayNumber}/${monthNumber}`,
        });
    }

    return labels;
};

/**
 * Filtre les dates selon les jours sélectionnés
 * @param dates Tableau de toutes les dates
 * @param selectedDays Tableau de booléens indiquant les jours visibles
 * @returns Dates filtrées selon selectedDays
 */
export const filterDatesBySelectedDays = (dates: string[], selectedDays: boolean[]): string[] => {
    return dates.filter((_, index) => selectedDays[index]);
};

/**
 * Filtre les labels selon les jours sélectionnés
 * @param labels Tableau de tous les labels
 * @param selectedDays Tableau de booléens indiquant les jours visibles
 * @returns Labels filtrés selon selectedDays
 */
export const filterLabelsBySelectedDays = (
    labels: Array<{ weekday: string; shortDate: string }>,
    selectedDays: boolean[],
): Array<{ weekday: string; shortDate: string }> => {
    return labels.filter((_, index) => selectedDays[index]);
};

/**
 * Calcule le lundi de la semaine pour une date donnée.
 * @param date La date pour laquelle trouver le lundi de la semaine.
 * @returns La date du lundi de la semaine.
 */
export const getMondayOfWeek = (date: Date): Date => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Calculate days to subtract to get to Monday

    const monday = new Date(date);
    monday.setDate(date.getDate() - diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
};

/**
 * Vérifie si une date est dans une période de fermeture du site
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param closingDatesRanges Les périodes de fermeture du site
 * @returns true si le site est fermé à cette date
 */
export const isDateInClosingRange = (
    date: BirthdayDate,
    closingDatesRanges: Array<{ start?: FrenchDate | null; end?: FrenchDate | null }>,
): boolean => {
    if (!closingDatesRanges || closingDatesRanges.length === 0) {
        return false;
    }

    const birthdayFormattedDate = date;

    return closingDatesRanges.some(({ start, end }) => {
        if (!start || !end) return false;

        const startBirthdayDate = transformFrenchToBirthDayDate(start);
        const endBirthdayDate = transformFrenchToBirthDayDate(end);

        return birthdayFormattedDate >= startBirthdayDate && birthdayFormattedDate < endBirthdayDate;
    });
};

/**
 * Vérifie si une date est un jour fermé (TotemClosedDay global)
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param totemClosedDays Les jours fermés globaux
 * @returns true si c'est un jour fermé global
 */
export const isDateInTotemClosedDays = (
    date: BirthdayDate,
    totemClosedDays: Array<{ date: BirthdayDate }>,
): boolean => {
    if (!totemClosedDays || totemClosedDays.length === 0) {
        return false;
    }

    return totemClosedDays.some(({ date: closedDate }) => closedDate === date);
};

/**
 * Vérifie si une date est avant la date de lancement du site
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param launchDate La date de lancement au format DD/MM/YYYY ou null
 * @returns true si la date est avant la date de lancement
 */
export const isDateBeforeLaunchDate = (date: BirthdayDate, launchDate: FrenchDate | null): boolean => {
    if (!launchDate) {
        return true; // Si pas de launchDate, tout est grisé
    }

    const launchDateISO = transformFrenchToBirthDayDate(launchDate);
    return date < launchDateISO;
};

/**
 * Vérifie si une date est après la date de suppression du site
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param removalDate La date de suppression au format YYYY-MM-DD ou null
 * @returns true si la date est après la date de suppression
 */
export const isDateAfterRemovalDate = (date: BirthdayDate, removalDate: BirthdayDate | null): boolean => {
    if (!removalDate) {
        return false; // Si pas de removalDate, rien n'est grisé pour cette raison
    }

    return date > removalDate;
};

/**
 * Vérifie si une date correspond exactement à la launchDate ou removalDate (impossible à décocher)
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param launchDate La date de lancement du site au format DD/MM/YYYY
 * @param removalDate La date de suppression du site au format YYYY-MM-DD
 * @returns true si la date est une launchDate ou removalDate exacte
 */
export const isDateLaunchOrRemoval = (
    date: BirthdayDate,
    launchDate?: FrenchDate | null,
    removalDate?: BirthdayDate | null,
): boolean => {
    const launch = launchDate && transformFrenchToBirthDayDate(launchDate);
    return date === launch || date === removalDate;
};

/**
 * Vérifie si une date est fermée
 * @param date La date à vérifier au format YYYY-MM-DD
 * @param closingDatesRanges Les périodes de fermeture du site
 * @param totemClosedDays Les jours fermés globaux
 * @param launchDate La date de lancement du site au format DD/MM/YYYY
 * @param removalDate La date de suppression du site au format YYYY-MM-DD
 * @returns true si la date est fermée
 */
export const isDateClosed = (
    date: BirthdayDate,
    closingDatesRanges: Array<{ start?: FrenchDate | null; end?: FrenchDate | null }>,
    totemClosedDays: Array<{ date: BirthdayDate }>,
    launchDate?: FrenchDate | null,
    removalDate?: BirthdayDate | null,
): boolean => {
    return (
        isDateInClosingRange(date, closingDatesRanges) ||
        isDateInTotemClosedDays(date, totemClosedDays) ||
        isDateBeforeLaunchDate(date, launchDate ?? null) ||
        isDateAfterRemovalDate(date, removalDate ?? null)
    );
};

/**
 * Calcule les dates pour 3 semaines consécutives (précédente, actuelle, suivante) à partir d'un lundi
 * @param mondayDate La date du lundi de la semaine de référence
 * @returns Un objet contenant les dates des 3 semaines et les métadonnées
 */
export const getThreeWeeksDates = (mondayDate: Date) => {
    const previousMondayDate = addDaysToDate({ date: mondayDate, daysToAdd: -7 });
    const nextMondayDate = addDaysToDate({ date: mondayDate, daysToAdd: 7 });

    const previousWeekDates = getWeekDates(previousMondayDate);
    const currentWeekDates = getWeekDates(mondayDate);
    const nextWeekDates = getWeekDates(nextMondayDate);

    return {
        previousWeek: {
            dates: previousWeekDates,
            mondayDate: previousMondayDate,
        },
        currentWeek: {
            dates: currentWeekDates,
            mondayDate: mondayDate,
        },
        nextWeek: {
            dates: nextWeekDates,
            mondayDate: nextMondayDate,
        },
        allDates: [...previousWeekDates, ...currentWeekDates, ...nextWeekDates],
    };
};

/**
 * Calcule les labels pour 3 semaines avec délimitations
 * @param mondayDate La date du lundi de la semaine de référence
 * @returns Un objet contenant les labels des 3 semaines et les métadonnées
 */
export const getThreeWeeksLabels = (mondayDate: Date) => {
    const { previousWeek, currentWeek, nextWeek } = getThreeWeeksDates(mondayDate);

    const previousWeekLabels = getWeekLabels(previousWeek.mondayDate);
    const currentWeekLabels = getWeekLabels(currentWeek.mondayDate);
    const nextWeekLabels = getWeekLabels(nextWeek.mondayDate);

    return {
        previousWeek: {
            labels: previousWeekLabels,
            mondayDate: previousWeek.mondayDate,
        },
        currentWeek: {
            labels: currentWeekLabels,
            mondayDate: currentWeek.mondayDate,
        },
        nextWeek: {
            labels: nextWeekLabels,
            mondayDate: nextWeek.mondayDate,
        },
        allLabels: [...previousWeekLabels, ...currentWeekLabels, ...nextWeekLabels],
    };
};

/**
 * Filtre les dates et labels de 3 semaines selon les jours sélectionnés
 * @param threeWeeksData Les données des 3 semaines
 * @param threeWeeksLabels Les labels des 3 semaines
 * @param selectedDays Tableau de booléens indiquant les jours visibles
 * @returns Dates et labels filtrés avec métadonnées de délimitation
 */
export const filterThreeWeeksBySelectedDays = (
    threeWeeksData: ReturnType<typeof getThreeWeeksDates>,
    threeWeeksLabels: ReturnType<typeof getThreeWeeksLabels>,
    selectedDays: boolean[],
) => {
    const filteredPreviousWeekDates = filterDatesBySelectedDays(threeWeeksData.previousWeek.dates, selectedDays);
    const filteredCurrentWeekDates = filterDatesBySelectedDays(threeWeeksData.currentWeek.dates, selectedDays);
    const filteredNextWeekDates = filterDatesBySelectedDays(threeWeeksData.nextWeek.dates, selectedDays);

    const filteredPreviousWeekLabels = filterLabelsBySelectedDays(threeWeeksLabels.previousWeek.labels, selectedDays);
    const filteredCurrentWeekLabels = filterLabelsBySelectedDays(threeWeeksLabels.currentWeek.labels, selectedDays);
    const filteredNextWeekLabels = filterLabelsBySelectedDays(threeWeeksLabels.nextWeek.labels, selectedDays);

    const weekDelimiters: Array<{
        position: number;
        weekType: 'previous' | 'current' | 'next';
        mondayDate: Date;
    }> = [];

    let currentPosition = 0;

    if (filteredPreviousWeekDates.length > 0) {
        weekDelimiters.push({
            position: currentPosition,
            weekType: 'previous',
            mondayDate: threeWeeksData.previousWeek.mondayDate,
        });
        currentPosition += filteredPreviousWeekDates.length;
    }

    if (filteredCurrentWeekDates.length > 0) {
        weekDelimiters.push({
            position: currentPosition,
            weekType: 'current',
            mondayDate: threeWeeksData.currentWeek.mondayDate,
        });
        currentPosition += filteredCurrentWeekDates.length;
    }

    if (filteredNextWeekDates.length > 0) {
        weekDelimiters.push({
            position: currentPosition,
            weekType: 'next',
            mondayDate: threeWeeksData.nextWeek.mondayDate,
        });
    }

    return {
        visibleDates: [...filteredPreviousWeekDates, ...filteredCurrentWeekDates, ...filteredNextWeekDates],
        visibleLabels: [...filteredPreviousWeekLabels, ...filteredCurrentWeekLabels, ...filteredNextWeekLabels],
        weekDelimiters,
        weekBreakdowns: {
            previousWeek: {
                dates: filteredPreviousWeekDates,
                labels: filteredPreviousWeekLabels,
                mondayDate: threeWeeksData.previousWeek.mondayDate,
            },
            currentWeek: {
                dates: filteredCurrentWeekDates,
                labels: filteredCurrentWeekLabels,
                mondayDate: threeWeeksData.currentWeek.mondayDate,
            },
            nextWeek: {
                dates: filteredNextWeekDates,
                labels: filteredNextWeekLabels,
                mondayDate: threeWeeksData.nextWeek.mondayDate,
            },
        },
    };
};
